import { faDownload, faEye, faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomButton from "components/button/CustomButton";
import DataTable, { Row } from "components/DataTable";
import { motion } from "framer-motion";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import httpClient from "services/network/httpClient";
import DemoReportFilter, { TotalSummaryFormProps, FilterFormValues } from "./demo/DemoReportFilter";
import axios from "axios";
import { getDefaultDateRange } from "pages/report/ReportList";
import FilterBadge, { FilterChip } from "components/filter/FilterBadge";
import appConfig from "services/config";
import GenericModal from "components/Modal";
import ViewVisitDetails from "pages/dashboard/visit/ViewVisitDetails";
import { msToDateInput } from "./DemoReport"

import DateTimeCell from "components/DataTable/DateTimeCell";
import FollowUpDateTimeCell from "components/DataTable/FolloUpDateTimeCell";

// Utility to convert ms/epoch to YYYY-MM-DD string for input fields and chips


interface VisitData {
    id: number;
    distributorName: string;
    imageUrl?: string;
    latitude: number;
    longitude: number;
    note: string;
    createdDate: number;
    nextFollowUpVisit?: number;
    employeeName?: string;
    employeeId?: number;
    isPending?: boolean;
}

interface VisitActionsProps {
    row: any;
    onView: (row: any) => void;
    onDownload: (row: any) => void
}
export const getFileNameFromDisposition = (disposition: any) => {
    if (!disposition) return null;

    // handle both filename= and filename*=
    const fileNameMatch =
        disposition.match(/filename\*=UTF-8''(.+)/) ||
        disposition.match(/filename="?([^"]+)"?/);

    return fileNameMatch?.[1] || null;
};

const VisitActionsCell: React.FC<VisitActionsProps> = ({ row, onView, onDownload }) => (
    <div className="flex space-x-2">
        <button
            onClick={() => onView(row._originalData)}
            className="px-3 py-1
        bg-blue-50 dark:bg-blue-900/30
        hover:bg-blue-100 dark:hover:bg-blue-900/50
        text-blue-700 dark:text-blue-300
        rounded-lg
        transition-colors duration-200
        flex items-center"
        >
            <FontAwesomeIcon icon={faEye} className="mr-1" />
        </button>
        <button
            onClick={() => onDownload(row._originalData)}
            className="px-3 py-1
        bg-blue-50 dark:bg-blue-900/30
        hover:bg-blue-100 dark:hover:bg-blue-900/50
        text-blue-700 dark:text-blue-300
        rounded-lg
        transition-colors duration-200
        flex items-center"
        >
            <FontAwesomeIcon icon={faDownload} className="mr-1" />
        </button>
    </div>
);

const VisitReport = () => {
    const [visitList, setVisitList] = useState<Row[]>([]);
    const [isFilterOpened, setIsFilterOpened] = useState(false)
    const [activeFilterParams, setActiveFilterParams] = useState<Record<string, string>>({});
    const activeFilterParamsRef = useRef<Record<string, string>>({});
    const [pageNumber, setPageNumber] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [filterVersion, setFilterVersion] = useState(0);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [activeFilterFormValues, setActiveFilterFormValues] = useState<Partial<FilterFormValues>>({});
    const [filterChips, setFilterChips] = useState<FilterChip[]>([]);

    const getVisit = async (overrideParams?: Record<string, string>) => {
        const baseParams: Record<string, string> = overrideParams
            ? { ...overrideParams, page: "1", size: String(itemsPerPage) }
            : (() => {
                const p: Record<string, string> = Object.keys(activeFilterParams).length > 0
                    ? { ...activeFilterParams }
                    : (() => {
                        const { fromDate, toDate } = getDefaultDateRange();
                        return { fromDate, toDate };
                    })();
                p.page = String(pageNumber);
                p.size = String(itemsPerPage);
                return p;
            })();
        setLoading(true);

        await httpClient.get(`v1/visit/team/all-visits?${new URLSearchParams(baseParams).toString()}`)
            .then((response: any) => {
                console.log("Getting Visit List: ", response);
                if (response.success) {
                    setVisitList(response.value.contents ?? []);
                    setTotalItems(response.value.totalElements ?? 0);
                }
            })
            .catch((error) => {
                console.log("Error Getting Visit:", error);
            })
            .finally(() => {
                setLoading(false);
            })
    }
    const handleView = (row: any) => {
        setIsView(true);
        const viewData = {
            ...row,
            employeeId: String(row.employeeId ?? ""),
            firstName: row.employeeName ?? "",
            city: row.city ?? "",
            totalVisits: 0,
            distributorName: row.distributorName,
            imageUrl: row.imageUrl,
            latitude: row.latitude,
            longitude: row.longitude,
            note: row.note,
            createdDate: row.createdDate,
            nextFollowUpVisit: row.nextFollowUpVisit,
        };
        console.log(viewData);
        setViewData(viewData);
    }

    const handleDownload = useCallback((row: VisitData) => {
        if (!row) {
            console.error("Attempted to download details for undefined row");
            return;
        }
        const token = localStorage.getItem("accessToken");
        const baseUrl = appConfig.getBaseUrl();

        axios.get(`${baseUrl}/reports/visit/${row.id}/pdf`, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/pdf" })
                );
                const disposition = response.headers["content-disposition"];
                // const fileName = disposition
                //     ? (disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/)?.[2]?.trim() ?? `demo-report-${row.id}.pdf`)
                //     : `demo-report-${row.id}.pdf`;
                const fileName = getFileNameFromDisposition(disposition);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Download PDF error: ", error);
            });
    }, []);

    const renderActions = useCallback(
        (row: any) => <VisitActionsCell row={row} onView={handleView} onDownload={handleDownload} />,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const sortedVisitData = useMemo(
        () => visitList.map(item => ({ ...item, _originalData: item })) as unknown as Row[],
        [visitList]
    );


    useEffect(() => {
        getVisit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, itemsPerPage, filterVersion]);
    const colomn = [
        {
            key: "managerName",
            label: "Manager Name",
            render: (_: any, row: any) => (
                <div className="flex flex-row item-center">
                    <div>
                        <div className="font-medium text-sm text-gray-800 dark:text-gray-200">{row.managerName || 'Direct'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.managerCode && row.managerCode}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: "employeeName",
            label: "Employee Name",
            render: (_: any, row: any) => (
                <div className="flex flex-row item-center">
                    <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">{row.employeeName || 'Direct'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.employeeCode && row.employeeCode}
                        </div>
                    </div>
                </div>
                // <span>{row.employeeCode ? `${row.employeeCode}/${row.employeeName}` : row.employeeName}</span>
            ),
        },

        {
            key: "distributorName",
            label: "Distributor Name",
            render: (_: any, row: any) => (
                <div className="flex flex-row item-center">
                    <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">{row.distributorName || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.distributorId && row.distributorId}
                        </div>
                    </div>
                </div>
                // <span>{row.distributorId ? `${row.distributorId}/${row.distributorName}` : row.distributorId}</span>
            )
        },
        {
            key: "createdDate",
            label: "Created",
            render: (value: ReactNode) => (
                <DateTimeCell value={value as number} />
            )
        },
        {
            key: "completedDate",
            label: "Completed",
            render: (value: ReactNode, row: any) => (
                <DateTimeCell value={value as number} row={row} />
            )
        },
        {
            key: "nextFollowUpVisit",
            label: "Follow Up",
            render: (value: ReactNode, row: any) => (
                <FollowUpDateTimeCell value={value as number} />
            )
        },

        {
            key: "actions",
            label: "Actions",
            excludeExport: true,
            actions: renderActions,
        },
    ]

    const handleFilterClose = () => {
        setIsFilterOpened(false);


    }

    const onSubmit = (data: TotalSummaryFormProps) => {
        // Build params — only include fields the user actually filled in
        const params: Record<string, string> = {};
        if (data.fromDate) params.fromDate = `${data.fromDate}`;
        if (data.toDate) params.toDate = `${data.toDate}`;
        if (data.fromDate) params.fromDate = `${data.fromDate}`;
        if (data.toDate) params.toDate = `${data.toDate}`;
        if (data.manager && data.manager !== "All") params.managerId = data.manager;
        if (data.employee && data.employee !== "All") params.empIds = data.employee;
        if ((data as any).product && (data as any).product !== "All") params.productId = (data as any).product;
        if ((data as any).crop && (data as any).crop !== "All") params.cropId = (data as any).crop;
        if ((data as any).status === 0 || (data as any).status === 1 && (data as any).status !== "All") params.status = (data as any).status;
        if ((data as any).distributor && (data as any).distributor !== "All") params.distributor = (data as any).distributor;

        // If no dates provided by user, fall back to default range
        if (!params.fromDate && !params.toDate) {
            const { fromDate, toDate } = getDefaultDateRange();
            params.fromDate = fromDate;
            params.toDate = toDate;
        }

        // Store for reuse (e.g. Download button)
        activeFilterParamsRef.current = params;
        setActiveFilterParams(params);
        // Always store as string for popup
        setActiveFilterFormValues({
            ...data,
            fromDate: msToDateInput(params.fromDate),
            toDate: msToDateInput(params.toDate),
        });

        // Build filter chips
        const chips: FilterChip[] = [];
        if (params.fromDate || params.toDate) {
            chips.push({
                id: "dateRange",
                label: `Date: ${msToDateInput(params.fromDate)} – ${msToDateInput(params.toDate)}`,
                onRemove: () => removeChip("dateRange", ["fromDate", "toDate"], ["fromDate", "toDate"]),
            });
        }
        if (params.managerId) {
            chips.push({
                id: "manager",
                label: `Manager: ${(data as any).__managerLabel ?? params.managerId}`,
                onRemove: () => removeChip("manager", ["managerId"], ["manager"]),
            });
        }
        if (params.empIds) {
            chips.push({
                id: "employee",
                label: `Employee: ${(data as any).__employeeLabel ?? params.empIds}`,
                onRemove: () => removeChip("employee", ["empIds"], ["employee"]),
            });
        }
        if (params.status !== undefined) {
            chips.push({
                id: "status",
                label: `Status: ${(data as any).__statusLabel ?? params.status}`,
                onRemove: () => removeChip("status", ["status"], ["status"]),
            });
        }
        setFilterChips(chips);

        setPageNumber(1);
        setFilterVersion(v => v + 1);
        setIsFilterOpened(false);
    };

    const handlePageChange = (newPage: number) => {
        setPageNumber(newPage);
    };

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setPageNumber(1);
    };

    const handleClearFilter = () => {
        activeFilterParamsRef.current = {};
        setActiveFilterParams({});
        setActiveFilterFormValues({});
        setFilterChips([]);
        setPageNumber(1);
        setFilterVersion(v => v + 1);
    };

    const removeChip = (chipId: string, paramKeys: string[], formKeys: string[]) => {
        // Read from ref — always current, even when called from a stale closure
        const newParams = { ...activeFilterParamsRef.current };
        if (chipId === "dateRange") {
            const { fromDate, toDate } = getDefaultDateRange();
            newParams.fromDate = fromDate;
            newParams.toDate = toDate;
        } else {
            paramKeys.forEach(k => delete newParams[k]);
        }
        setFilterChips(prev => prev.filter(c => c.id !== chipId));
        activeFilterParamsRef.current = newParams;
        setActiveFilterParams(newParams);
        setActiveFilterFormValues(prev => {
            const next = { ...prev } as any;
            // Always store as string for popup
            next.fromDate = msToDateInput(newParams.fromDate);
            next.toDate = msToDateInput(newParams.toDate);
            formKeys.forEach(k => { if (k !== "fromDate" && k !== "toDate") delete next[k]; });
            return next;
        });
        setPageNumber(1);
        getVisit(newParams);
    };

    const handleDownloadExcel = async () => {
        const token = localStorage.getItem("accessToken");
        const baseUrl = appConfig.getBaseUrl();
        console.log("Base Url from Visit report: ", baseUrl);

        // Use active filter params if the user applied a filter, otherwise fall back to default date range
        const params: Record<string, string> = Object.keys(activeFilterParams).length > 0
            ? { ...activeFilterParams }
            : (() => {
                const { fromDate, toDate } = getDefaultDateRange();
                return { fromDate, toDate };
            })();
        setIsDownloading(true);

        await axios.get(
            `${baseUrl}/v1/visit/team/export?${new URLSearchParams(params).toString()}`,
            {
                responseType: "blob",
                headers: { Authorization: `Bearer ${token}` },
            }
        )
            .then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                );
                const disposition = response.headers["content-disposition"];
                // const fileName = disposition
                //     ? (disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/)?.[2]?.trim() ?? `report-${Date.now()}.xlsx`)
                //     : `report-${Date.now()}.xlsx`;
                const fileName = getFileNameFromDisposition(disposition);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Generate Report error: ", error);
            })
            .finally(() => {
                setIsDownloading(false);
            })
    };

    return (
        <div className="flex flex-col w-full">
            <header className="flex w-full flex-row items-center justify-between mb-4 px-2">
                <section className="item-start w-[75%]">
                    <h2 className="text-gray-800 dark:text-white text-xl font-semibold">Visit List</h2>
                </section>
                <section className="item-end flex flex-row items-center justify-end gap-4 w-[25%]">
                    <div className="relative">
                        {/* <CustomButton>Filter</CustomButton> */}
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            // ref={setFilterBtnRef as any}
                            onClick={() => setIsFilterOpened(!isFilterOpened)}
                            className="relative px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors duration-200 flex items-center"
                        >
                            <FontAwesomeIcon icon={faFilter} className="mr-1.5" />
                            <span>Filter</span>
                            {filterChips.length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white font-bold">
                                    {filterChips.length}
                                </span>
                            )}
                        </motion.button>
                        {isFilterOpened && (
                            <div
                                style={{
                                    position: 'absolute',
                                    zIndex: 50,
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    width: '400px',
                                    maxWidth: 'calc(100vw - 2rem)'
                                }}
                            >
                                <DemoReportFilter onClose={handleFilterClose} title="Generate Visit Report" onSubmit={onSubmit} onReset={handleClearFilter} showStatus={true} initialValues={activeFilterFormValues}></DemoReportFilter>
                            </div>
                        )}

                    </div>
                    <div>
                        <CustomButton onClick={handleDownloadExcel} loading={isDownloading}>
                            {
                                !isDownloading && <FontAwesomeIcon icon={faDownload} className="mr-1.5" />

                            } Download</CustomButton>
                    </div>
                </section>
            </header>
            <FilterBadge count={filterChips.length} onClear={handleClearFilter} chips={filterChips} />
            <main className="w-full h-full border ">
                <DataTable
                    columns={colomn}
                    data={sortedVisitData}
                    isAddBtnTrue={false}
                    isSearchBtn={false}
                    isExport={true}
                    serverSide={true}
                    currentPage={pageNumber}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    loading={loading}
                ></DataTable>
            </main>
            {
                isView && (
                    <GenericModal
                        isOpen={isView}
                        onClose={() => setIsView(false)}
                        header={`Visit Details for ${viewData?.employeeName}`}
                        size="xl"
                        // customWidth="md:max-w-lg"
                        customWidth="md:max-w-6xl"
                    >
                        <ViewVisitDetails
                            selectedVisit={viewData}
                            handleDownload={() => console.log("hello")}
                            handleDeleteIcon={() => console.log("hello")}
                            isDeletedMedia={(value: any) => true}
                        />
                    </GenericModal>
                )
            }


        </div>
    )
}

export default VisitReport